import { useToast } from '@chakra-ui/react';
import { showToast } from '@diamond/shared/ui';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { VOUCHER_TOAST_MESSAGES } from '../constants';
import type {
  CreateVoucherRequest,
  VoucherUpdateBasicInfoPayload,
} from '../types';
import { useUpdateVoucherMutation } from './api-hooks';

interface UseVoucherUpdateProps {
  voucherId: string;
  form: UseFormReturn<CreateVoucherRequest>;
  onSuccess?: () => void;
}

export function useVoucherUpdate({
  voucherId,
  form,
  onSuccess,
}: UseVoucherUpdateProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const session = useAuthStore();

  const updateMutation = useUpdateVoucherMutation({
    onSuccess: () => {
      showToast(toast, 'success', 'Voucher berhasil diperbarui');
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (
      error: Error & {
        response?: { data?: { message?: string; error?: string } };
      }
    ) => {
      // Extract error message from API response
      const apiMessage =
        error.response?.data?.message || error.response?.data?.error;
      const errorMessage =
        apiMessage || error.message || 'Gagal memperbarui voucher';
      showToast(toast, 'error', errorMessage);
    },
  });

  const handleUpdate = useCallback(async () => {
    const formData = form.getValues();

    // Transform form data to API payload (only editable fields)
    const payload: VoucherUpdateBasicInfoPayload = {
      banner_url: formData.banner_url,
      title: formData.title,
      desc: formData.desc,
      period_start_date: formData.period_start_date,
      period_end_date: formData.period_end_date,
      target_configuration: {
        quota: formData.target_configuration.quota,
        max_use_per_user: formData.target_configuration.max_use_per_user,
      },
    };

    await updateMutation.mutateAsync({ id: voucherId, data: payload });
  }, [form, voucherId, updateMutation]);

  return {
    handleUpdate,
    isUpdating: updateMutation.isPending,
  };
}
